import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Container, HtmlContent } from './ui';
import BeeBadge from './BeeBadge';

const StyledSubBanner = styled.section`
  position: relative;
  display: grid;
  color: ${standardColours.white};
  background-color: ${standardColours.black};
`;

const StyledImage = styled(GatsbyImage)`
  grid-area: 1 / 1 / 1 / 1;
  max-height: ${({
    $isShorter,
    $isImageContentBannerSlider,
    $isDefaultPage,
  }) =>
    ($isDefaultPage && !$isShorter) || $isImageContentBannerSlider
      ? '1080'
      : '750'}px;

  ${maxBreakpointQuery.large`
    opacity: 0.9;
  `}

  ${maxBreakpointQuery.small`
    opacity: 0.8;
  `}
`;

const StyledOuter = styled.div`
  grid-area: 1 / 1 / 1 / 1;
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;

  ${({ $isImageContentBannerSlider }) => {
    if ($isImageContentBannerSlider) {
      return css`
        padding-bottom: 100px;

        ${minBreakpointQuery.small`
          padding-top: 100px;
          padding-left: 100px;
        `}
      `;
    }
  }}
`;

const StyledInner = styled.div`
  ${sectionPaddings('60px', '120px')};
`;

const StyledContent = styled.div`
  max-width: ${({ $isCommunityStoryListingPage }) =>
    $isCommunityStoryListingPage ? '600' : '900'}px;

  ${minBreakpointQuery.tiny`
    width: 90%;
  `}

  ${minBreakpointQuery.medium`
    width: 80%;
  `}

  ${minBreakpointQuery.xxlarge`
    width: 70%;
  `}
`;

const StyledOverline = styled.p`
  margin-bottom: 10px;
  ${fluidFontSize(
    '24px',
    '52px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};

  ${minBreakpointQuery.small`
    margin-bottom: 12px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 14px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 16px;
  `}
`;

const StyledHeading = styled.h1`
  ${({ $isDefaultPage, $isShorter }) =>
    fluidFontSize(
      $isDefaultPage && !$isShorter ? '50px' : '36px',
      $isDefaultPage && !$isShorter ? '140px' : '80px',
      breakpointSizes.tiny,
      breakpointSizes.xxxxlarge,
    )}
  line-height: 1;
`;

const StyledText = styled(HtmlContent)`
  margin-top: 15px;

  ${minBreakpointQuery.tiny`
    margin-top: 20px;
    width: 90%;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 25px;
    width: 80%;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 30px;
    width: 70%;
  `}

  p {
    ${fluidFontSize(
      '16px',
      '28px',
      breakpointSizes.tiny,
      breakpointSizes.xxxxlarge,
    )}
  }

  b {
    color: ${brandColours.primary};
  }
`;

const StyledBadge = styled(BeeBadge)`
  --badge-width: 100px;
  position: absolute;
  top: calc(100% - (var(--badge-width) / 2));
  padding: 10px;
  max-width: var(--badge-width);
  background-color: ${standardColours.white};
  border-radius: 50%;
  transform: rotate(-60deg);

  ${minBreakpointQuery.small`
   --badge-width: 130px;
    padding: 14px;
  `}

  ${minBreakpointQuery.medium`
    --badge-width: 160px;
    padding: 18px;
  `}

  ${minBreakpointQuery.large`
    --badge-width: 200px;
    padding: 22px;
  `}

  ${minBreakpointQuery.xxlarge`
    --badge-width: 240px;
    padding: 26px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    --badge-width: 300px;
    padding: 30px;
  `}
`;

const SubBanner = ({
  heading,
  overline,
  text,
  image,
  isShorter,
  isImageContentBannerSlider,
  isDefaultPage,
  isCommunityStoryListingPage,
}) => (
  <StyledSubBanner>
    <StyledImage
      image={image.gatsbyImageData}
      alt={image.title}
      {...(!isImageContentBannerSlider && {
        loading: 'eager',
        fetchpriority: 'high',
      })}
      $isShorter={isShorter}
      $isImageContentBannerSlider={isImageContentBannerSlider}
      $isDefaultPage={isDefaultPage}
    />
    <StyledOuter $isImageContentBannerSlider={isImageContentBannerSlider}>
      <Container>
        <StyledInner>
          <StyledContent
            $isCommunityStoryListingPage={isCommunityStoryListingPage}
          >
            {overline && <StyledOverline>{overline}</StyledOverline>}
            <StyledHeading
              $isDefaultPage={isDefaultPage}
              $isShorter={isShorter}
            >
              {heading}
            </StyledHeading>
            {text && (
              <StyledText
                content={text}
                $isShorter={isShorter}
                $isDefaultPage={isDefaultPage}
              />
            )}
          </StyledContent>
        </StyledInner>
        {isDefaultPage && !isShorter && <StyledBadge />}
      </Container>
    </StyledOuter>
  </StyledSubBanner>
);

export default SubBanner;

export const SubBannerLargeImageFragment = graphql`
  fragment SubBannerLargeImageFragment on ContentfulAsset {
    gatsbyImageData(width: 1800, height: 1080, layout: FULL_WIDTH)
    title
  }
`;

export const SubBannerSmallImageFragment = graphql`
  fragment SubBannerSmallImageFragment on ContentfulAsset {
    gatsbyImageData(width: 1800, height: 750, layout: FULL_WIDTH)
    title
  }
`;
